import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { API } from "@/lib/brand";
import MoneyInput from "@/components/MoneyInput";
import InfoTooltip from "@/components/InfoTooltip";
import {
  CalendarCheck,
  CheckCircle,
  Sparkle,
  PencilSimple,
  ArrowClockwise,
  Trophy,
} from "@phosphor-icons/react";

// Iter35b — Weekly Check-in tab content. Lives under /actuals as a tab.
// A pulse belongs to the month its Monday falls in.
//
// Iter38 — LAG BY ONE WEEK. Users only ever log completed weeks (Sun in the
// past). The "primary" card is LAST completed week (Mon–Sun); the
// "secondary" card is the week before that. The current, in-progress
// calendar week is never shown here — you can't sensibly log a week that
// hasn't finished yet. Labels reflect this: card 1 says "Last week" and
// card 2 says "The week before".

const FEELINGS = [
  { key: "on-track", emoji: "😌", label: "On track" },
  { key: "tight", emoji: "😬", label: "Tight" },
  { key: "ahead", emoji: "🎉", label: "Ahead" },
];

function ProgressBar({ pct, tone = "yellow", testid }) {
  const clamped = pct == null ? 0 : Math.max(0, Math.min(150, pct));
  const overCap = clamped > 100;
  const fillWidth = Math.min(100, clamped);
  const barColour = overCap
    ? "bg-emerald-500"
    : tone === "red"
    ? "bg-red-500"
    : tone === "yellow"
    ? "bg-[#FFBD4A]"
    : "bg-black";
  return (
    <div className="w-full h-3 border-[1.5px] border-black rounded-full bg-white overflow-hidden" data-testid={testid}>
      <div
        className={`${barColour} h-full transition-all`}
        style={{ width: `${fillWidth}%` }}
      />
    </div>
  );
}

export default function WeeklyCheckIn() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pulses, setPulses] = useState([]);
  const [progress, setProgress] = useState(null);
  const [currentWeek, setCurrentWeek] = useState(null);
  const [previousWeek, setPreviousWeek] = useState(null);
  const [editingWeek, setEditingWeek] = useState(null); // week_start_iso or null
  const [form, setForm] = useState({
    revenue_in: "",
    expenses_out: "",
    cash_balance_eow: "",
    wins: "",
    challenges: "",
    feeling: "",
  });
  const [saving, setSaving] = useState(false);
  // Iter38 — Forecast Story button state machine.
  //   idle       → greyed-out "View Forecast Story" (nothing new to see)
  //   thinking   → yellow disabled "Thinking…" (regen in flight after save)
  //   fresh      → active yellow "View Forecast Story →" (deep-link to /forecast)
  const [storyBtn, setStoryBtn] = useState("idle");

  const load = async ({ initial = false } = {}) => {
    if (initial) setLoading(true);
    try {
      const [pulsesRes, progressRes, storyRes] = await Promise.all([
        axios.get(`${API}/pulses`),
        axios.get(`${API}/pulses/progress`),
        axios.get(`${API}/ai/forecast-story`).catch(() => ({ data: {} })),
      ]);
      setPulses(pulsesRes.data.pulses || []);
      setCurrentWeek(pulsesRes.data.current_week);
      setPreviousWeek(pulsesRes.data.previous_week);
      setProgress(progressRes.data);
      // If we're mid-flow (thinking), don't stomp on it here.
      setStoryBtn((prev) => {
        if (prev === "thinking") return prev;
        return storyRes.data?.can_regenerate ? "fresh" : "idle";
      });
    } catch (e) {
      toast.error("Could not load your check-ins");
    } finally {
      if (initial) setLoading(false);
    }
  };

  useEffect(() => {
    load({ initial: true });
  }, []);

  const pulseByWeek = useMemo(() => {
    const m = {};
    for (const p of pulses) m[p.week_start_iso] = p;
    return m;
  }, [pulses]);

  const startEdit = (weekIso) => {
    const existing = pulseByWeek[weekIso];
    setEditingWeek(weekIso);
    setForm({
      revenue_in: existing?.revenue_in ?? "",
      expenses_out: existing?.expenses_out ?? "",
      cash_balance_eow: existing?.cash_balance_eow ?? "",
      wins: existing?.wins ?? "",
      challenges: existing?.challenges ?? "",
      feeling: existing?.feeling ?? "",
    });
  };

  const cancel = () => {
    setEditingWeek(null);
    setForm({ revenue_in: "", expenses_out: "", cash_balance_eow: "", wins: "", challenges: "", feeling: "" });
  };

  const submit = async () => {
    if (!editingWeek) return;
    const revenue = Number(form.revenue_in);
    const expenses = Number(form.expenses_out);
    if (isNaN(revenue) || revenue < 0) {
      toast.error("Money in must be a number ≥ 0");
      return;
    }
    if (isNaN(expenses) || expenses < 0) {
      toast.error("Money out must be a number ≥ 0");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        week_start_iso: editingWeek,
        revenue_in: revenue,
        expenses_out: expenses,
        cash_balance_eow: form.cash_balance_eow === "" ? null : Number(form.cash_balance_eow),
        wins: form.wins || null,
        challenges: form.challenges || null,
        feeling: form.feeling || null,
      };
      await axios.post(`${API}/pulses`, payload);
      // Iter38 — no toast popup with an action button any more. Instead,
      // the sticky "View Forecast Story" button on this page swaps into
      // "Thinking…" while we regenerate in the background. When done, it
      // turns yellow and takes them to /forecast to read the fresh story.
      cancel();
      // Refresh cards + progress (but not the story state — we manage that below).
      load();
      // Kick the regen off in the background — we don't await this, so the
      // user gets an instant snappy UI while the LLM churns.
      setStoryBtn("thinking");
      (async () => {
        try {
          await axios.get(`${API}/ai/forecast-story?regenerate=true`);
          setStoryBtn("fresh");
        } catch (e) {
          // If regen fails (e.g. plan cap), quietly reset to idle. The
          // user's pulse still saved fine.
          setStoryBtn("idle");
        }
      })();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save your pulse");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card text-center text-black/60 py-12" data-testid="weekly-loading">
        Loading your check-ins…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="weekly-checkin-tab">
      {/* Intro band */}
      <div className="card-yellow" data-testid="weekly-intro">
        <div className="flex items-start gap-3">
          <CalendarCheck size={28} weight="duotone" className="shrink-0 mt-1" />
          <div>
            <h2 className="font-heading uppercase text-2xl">Monday morning ritual</h2>
            <p className="mt-2 text-sm text-black/80 leading-relaxed">
              15 minutes, coffee in hand. Log <strong>last week's</strong> cash rhythm — rough
              figures, no spreadsheet needed. This isn't accounting, it's a check-up. Weeks run{" "}
              <strong>Monday → Sunday</strong>, and you log a week once it's finished. Each pulse
              belongs to the month its Monday falls in.
            </p>
          </div>
        </div>
      </div>

      {/* Progress bars — pace vs forecast */}
      {progress && progress.pulses_this_month > 0 && (
        <div className="card" data-testid="weekly-progress-card">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="font-heading uppercase text-xl">Pace this month · {progress.month_label}</h3>
            <InfoTooltip label="Pace">
              We sum your weekly pulses so far this month and compare against your forecasted
              monthly total. Green = you've hit or beaten your target; yellow = on pace;
              red = falling behind.
            </InfoTooltip>
          </div>

          {/* Time-of-month reference */}
          <div className="text-xs text-black/60 mb-4">
            <strong>Day {progress.day_of_month} of {progress.days_in_month}</strong> · {progress.time_progress_pct}% of the month elapsed
          </div>

          {/* Revenue bar */}
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="font-semibold">Revenue</span>
              <span data-testid="weekly-revenue-figure">
                £{progress.total_revenue_in.toLocaleString("en-GB")}
                {progress.forecast_income != null && (
                  <span className="text-black/50">
                    {" "}
                    / £{progress.forecast_income.toLocaleString("en-GB")}
                    {progress.revenue_pct != null && (
                      <> · <strong>{progress.revenue_pct}%</strong></>
                    )}
                  </span>
                )}
              </span>
            </div>
            <ProgressBar
              pct={progress.revenue_pct}
              tone={
                progress.revenue_pct == null
                  ? "yellow"
                  : progress.revenue_pct < progress.time_progress_pct - 15
                  ? "red"
                  : "yellow"
              }
              testid="weekly-revenue-bar"
            />
          </div>

          {/* Expenses bar */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="font-semibold">Expenses</span>
              <span data-testid="weekly-expenses-figure">
                £{progress.total_expenses_out.toLocaleString("en-GB")}
                {progress.forecast_costs != null && (
                  <span className="text-black/50">
                    {" "}
                    / £{progress.forecast_costs.toLocaleString("en-GB")}
                    {progress.expenses_pct != null && (
                      <> · <strong>{progress.expenses_pct}%</strong></>
                    )}
                  </span>
                )}
              </span>
            </div>
            <ProgressBar
              pct={progress.expenses_pct}
              tone={
                progress.expenses_pct == null
                  ? "yellow"
                  : progress.expenses_pct > progress.time_progress_pct + 15
                  ? "red"
                  : "yellow"
              }
              testid="weekly-expenses-bar"
            />
          </div>

          {/* Iter38 — Forecast Story CTA. Always visible; state machine:
                idle     → greyed "View Forecast Story" (no fresh data to see yet)
                thinking → yellow disabled "Thinking…" (regen in flight)
                fresh    → active yellow "View Forecast Story →" (deep-link) */}
          {pulses.length > 0 && (
            <div className="mt-6 pt-5 border-t border-black/10" data-testid="weekly-story-cta">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-2 flex-1 min-w-[220px]">
                  <Sparkle size={18} weight="duotone" className="shrink-0 mt-1" />
                  <p className="text-sm text-black/75 leading-relaxed">
                    {storyBtn === "thinking" ? (
                      <>Weaving your latest pulse into a fresh Forecast Story — this usually
                      takes a few seconds. You can stay on this page.</>
                    ) : storyBtn === "fresh" ? (
                      <>Your Forecast Story has been refreshed with this week's texture.
                      Head over to read the new take on your outlook.</>
                    ) : (
                      <>Your Forecast Story is already up to date. Log a new week above to
                      unlock a fresh take.</>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => nav("/forecast#forecast-story")}
                  disabled={storyBtn !== "fresh"}
                  className={
                    storyBtn === "fresh"
                      ? "btn-primary shrink-0"
                      : storyBtn === "thinking"
                      ? "btn-primary shrink-0 opacity-70 cursor-wait"
                      : "btn-secondary shrink-0 opacity-60 cursor-not-allowed"
                  }
                  data-testid="weekly-view-story-btn"
                >
                  <Sparkle size={14} weight="fill" />
                  {storyBtn === "thinking" ? "Thinking…" : "View Forecast Story →"}
                </button>
              </div>
              <p className="mt-3 text-[11px] text-black/50 leading-snug italic">
                Insights refresh automatically when you add new data — weekly check-ins,
                monthly actuals, or forecast changes.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Last week / week before — the log cards. Iter38: always shows the
          TWO MOST-RECENTLY-COMPLETED weeks. Never the current in-progress
          week (you can't sensibly log a week that hasn't finished). */}
      <div className="grid md:grid-cols-2 gap-4">
        {[
          { data: currentWeek, isPrimary: true },
          { data: previousWeek, isPrimary: false },
        ].map(({ data, isPrimary }) => {
          if (!data) return null;
          const existing = pulseByWeek[data.week_start_iso];
          const isEditing = editingWeek === data.week_start_iso;
          const eyebrow = isPrimary ? "Last week" : "The week before";
          return (
            <div
              key={data.week_start_iso}
              className={`card ${isPrimary ? "border-2 border-black" : ""}`}
              data-testid={`weekly-card-${isPrimary ? "primary" : "secondary"}`}
            >
              <div className="section-eyebrow">{eyebrow}</div>
              <h3 className="font-heading uppercase text-lg mt-2">{data.week_label}</h3>

              {!isEditing && existing && (
                <div className="mt-4 space-y-3" data-testid="weekly-logged-summary">
                  <div className="flex items-center gap-2 text-emerald-800 text-sm font-semibold">
                    <CheckCircle size={16} weight="fill" /> Logged
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="border-[1.5px] border-black/15 rounded-lg p-2">
                      <div className="text-[10px] uppercase text-black/50">Money in</div>
                      <div className="font-heading text-lg">£{existing.revenue_in.toLocaleString("en-GB")}</div>
                    </div>
                    <div className="border-[1.5px] border-black/15 rounded-lg p-2">
                      <div className="text-[10px] uppercase text-black/50">Money out</div>
                      <div className="font-heading text-lg">£{existing.expenses_out.toLocaleString("en-GB")}</div>
                    </div>
                    <div className="border-[1.5px] border-black/15 rounded-lg p-2">
                      <div className="text-[10px] uppercase text-black/50">Balance</div>
                      <div className="font-heading text-lg">
                        {existing.cash_balance_eow != null
                          ? `£${existing.cash_balance_eow.toLocaleString("en-GB")}`
                          : "—"}
                      </div>
                    </div>
                  </div>
                  {(existing.wins || existing.challenges || existing.feeling) && (
                    <div className="space-y-2">
                      {existing.feeling && (
                        <div className="text-sm">
                          {FEELINGS.find((f) => f.key === existing.feeling)?.emoji}{" "}
                          <span className="text-black/60">
                            {FEELINGS.find((f) => f.key === existing.feeling)?.label}
                          </span>
                        </div>
                      )}
                      {existing.wins && (
                        <div className="text-sm border-l-2 border-[#FFBD4A] pl-2">
                          <span className="text-[10px] uppercase text-black/50 block">Win</span>
                          <span>{existing.wins}</span>
                        </div>
                      )}
                      {existing.challenges && (
                        <div className="text-sm border-l-2 border-black/25 pl-2">
                          <span className="text-[10px] uppercase text-black/50 block">Challenge</span>
                          <span>{existing.challenges}</span>
                        </div>
                      )}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => startEdit(data.week_start_iso)}
                    className="btn-secondary !py-1.5 !px-3 !text-xs mt-3"
                    data-testid={`weekly-edit-${isPrimary ? "primary" : "secondary"}`}
                  >
                    <PencilSimple size={12} weight="bold" /> Edit
                  </button>
                </div>
              )}

              {!isEditing && !existing && (
                <div className="mt-4 space-y-3">
                  <p className="text-sm text-black/60">
                    {isPrimary
                      ? "3 minutes. 4 fields. Log last week's cash rhythm and refresh your Forecast Story."
                      : "The week before that — no bother if you skipped it. Catch up in 3 minutes."}
                  </p>
                  <button
                    type="button"
                    onClick={() => startEdit(data.week_start_iso)}
                    className="btn-primary"
                    data-testid={`weekly-log-${isPrimary ? "primary" : "secondary"}-btn`}
                  >
                    {isPrimary ? "Log last week →" : "Log that week →"}
                  </button>
                </div>
              )}

              {isEditing && (
                <div className="mt-4 space-y-3" data-testid="weekly-form">
                  <div>
                    <label className="block text-xs font-semibold mb-1">Money IN this week</label>
                    <MoneyInput
                      value={form.revenue_in}
                      onChange={(v) => setForm({ ...form, revenue_in: v })}
                      testid="weekly-revenue-in"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1">Money OUT this week</label>
                    <MoneyInput
                      value={form.expenses_out}
                      onChange={(v) => setForm({ ...form, expenses_out: v })}
                      testid="weekly-expenses-out"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1 flex items-center gap-1">
                      <span>Cash balance at end of Sunday</span>
                      <InfoTooltip label="Cash balance">
                        Peek at your business bank balance last Sunday evening — one figure.
                        Helps forecastly cross-check your pulse against reality.
                      </InfoTooltip>
                    </label>
                    <MoneyInput
                      value={form.cash_balance_eow}
                      onChange={(v) => setForm({ ...form, cash_balance_eow: v })}
                      testid="weekly-balance-eow"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1">How did it feel?</label>
                    <div className="flex gap-2">
                      {FEELINGS.map((f) => (
                        <button
                          key={f.key}
                          type="button"
                          onClick={() => setForm({ ...form, feeling: form.feeling === f.key ? "" : f.key })}
                          className={`flex-1 border-[1.5px] rounded-lg py-2 text-sm transition ${
                            form.feeling === f.key
                              ? "border-black bg-[#FFBD4A] shadow-[2px_2px_0_#000]"
                              : "border-black/25 hover:bg-[#FFF7E5]"
                          }`}
                          data-testid={`weekly-feeling-${f.key}`}
                        >
                          <span className="text-lg">{f.emoji}</span>{" "}
                          <span className="text-xs">{f.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1">
                      Win this week <span className="text-black/50 font-normal">· optional</span>
                    </label>
                    <textarea
                      className="input min-h-[60px]"
                      value={form.wins}
                      onChange={(e) => setForm({ ...form, wins: e.target.value })}
                      placeholder="e.g. Landed a new client · Cleared my inbox · Said yes to the podcast"
                      maxLength={500}
                      data-testid="weekly-wins"
                    />
                    <div className="text-[10px] text-black/40 text-right">{(form.wins || "").length}/500</div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1">
                      Challenge this week <span className="text-black/50 font-normal">· optional</span>
                    </label>
                    <textarea
                      className="input min-h-[60px]"
                      value={form.challenges}
                      onChange={(e) => setForm({ ...form, challenges: e.target.value })}
                      placeholder="e.g. Two clients pushed invoices to next month · Had to buy new equipment"
                      maxLength={500}
                      data-testid="weekly-challenges"
                    />
                    <div className="text-[10px] text-black/40 text-right">{(form.challenges || "").length}/500</div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button
                      type="button"
                      onClick={submit}
                      disabled={saving}
                      className="btn-primary"
                      data-testid="weekly-save-btn"
                    >
                      {saving ? "Saving…" : existing ? "Update pulse" : "Save pulse"}
                    </button>
                    <button
                      type="button"
                      onClick={cancel}
                      className="btn-secondary"
                      data-testid="weekly-cancel-btn"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Recent history */}
      {pulses.length > 2 && (
        <div className="card" data-testid="weekly-history">
          <div className="flex items-center gap-2 mb-4">
            <Trophy size={20} weight="duotone" />
            <h3 className="font-heading uppercase text-lg">Recent pulses</h3>
          </div>
          <div className="divide-y divide-black/10">
            {pulses.slice(2, 12).map((p) => (
              <div
                key={p.week_start_iso}
                className="py-3 grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-3 text-sm items-center"
                data-testid={`weekly-history-${p.week_start_iso}`}
              >
                <div className="font-semibold">{p.week_label}</div>
                <div>
                  <span className="text-[10px] uppercase text-black/50 block sm:inline">In · </span>
                  £{p.revenue_in.toLocaleString("en-GB")}
                </div>
                <div>
                  <span className="text-[10px] uppercase text-black/50 block sm:inline">Out · </span>
                  £{p.expenses_out.toLocaleString("en-GB")}
                </div>
                <div>
                  {p.feeling && (
                    <span className="text-lg">
                      {FEELINGS.find((f) => f.key === p.feeling)?.emoji}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-center">
        <button
          type="button"
          onClick={load}
          className="text-xs text-black/50 hover:text-black underline inline-flex items-center gap-1"
          data-testid="weekly-refresh"
        >
          <ArrowClockwise size={12} weight="bold" /> Refresh
        </button>
      </div>
    </div>
  );
}
